import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, MessageCircle, PlusCircle, Clock } from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Reveal } from "../components/Reveal";
import { FeaturedMemory } from "../components/FeaturedMemory";

const CARD_HIDDEN = { opacity: 0, y: 32 };
const CARD_VISIBLE = { opacity: 1, y: 0 };
const CARD_VIEWPORT = { once: true, margin: "-40px" };
const cardTransition = (i) => ({ duration: 0.7, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] });

export const PostMedia = ({ post, className = "" }) =>
  post.media_type === "video" ? (
    <video src={mediaUrl(post.media_url)} controls preload="metadata" className={className} />
  ) : (
    <img src={mediaUrl(post.media_url)} alt={`${post.dog_name} — ricordo condiviso`} loading="lazy" className={className} />
  );

const Feed = () => {
  const [posts, setPosts] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/posts").then(({ data }) => setPosts(data)).finally(() => setLoading(false));
    api.get("/posts/featured").then(({ data }) => setFeatured(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) api.get("/posts/mine").then(({ data }) => setMine(data)).catch(() => {});
  }, [user]);

  const toggleLike = async (post) => {
    if (!user) {
      navigate("/accedi");
      return;
    }
    const { data } = await api.post(`/posts/${post.post_id}/like`);
    const patch = (p) => (p.post_id === post.post_id ? { ...p, liked_by_me: data.liked, likes_count: data.likes_count } : p);
    setPosts((prev) => prev.map(patch));
    setFeatured((f) => (f ? patch(f) : f));
  };

  const pendingMine = mine.filter((p) => p.status === "pending");

  return (
    <main className="mx-auto max-w-6xl px-4 pb-24 sm:px-8">
      <section className="py-14 sm:py-20">
        <Reveal>
          <p className="eyebrow flex items-center gap-3">
            <span className="inline-block h-px w-8 bg-[#B85328]" />
            La community della Casetta
          </p>
          <h1
            data-testid="feed-main-heading"
            className="font-display mt-4 max-w-2xl text-4xl font-bold leading-[1.08] tracking-tight text-[#2C2723] sm:text-5xl lg:text-6xl"
          >
            Le nuove vite dei <span className="italic text-[#B85328]">nostri bassotti</span>
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-[#5C534B] sm:text-lg">
            Le famiglie che hanno accolto un cucciolo della Casetta condividono qui foto e video
            della loro nuova vita insieme. Ogni ricordo è approvato dai quattro fondatori.
          </p>
          {user && (
            <Link
              to="/condividi"
              data-testid="feed-share-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#1F1A17] px-7 py-3.5 text-sm font-semibold text-[#FBF8F3] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#B85328] hover:shadow-xl"
            >
              <PlusCircle size={16} /> Condividi il tuo ricordo
            </Link>
          )}
        </Reveal>
      </section>

      {pendingMine.length > 0 && (
        <div data-testid="feed-pending-notice" className="mb-10 flex items-center gap-3 rounded-2xl bg-[#EAF0ED] px-5 py-4 text-sm text-[#4A6B5D]">
          <Clock size={16} className="shrink-0" />
          Hai {pendingMine.length} {pendingMine.length === 1 ? "ricordo in attesa" : "ricordi in attesa"} di approvazione
          dai fondatori. Appena approvato sarà visibile a tutti.
        </div>
      )}

      {loading && <p className="py-20 text-center font-display text-2xl italic text-[#8C7B6E]">Caricamento dei ricordi...</p>}
      {!loading && posts.length === 0 && (
        <div data-testid="feed-empty-state" className="rounded-3xl border border-dashed border-[#CFC0B2] py-24 text-center">
          <p className="font-display text-3xl italic text-[#5C534B]">Ancora nessun ricordo qui.</p>
          <p className="mt-3 text-sm text-[#8C7B6E]">
            {user ? "Sii il primo a condividere la nuova vita del tuo bassotto." : "Accedi per condividere il primo ricordo."}
          </p>
          {!user && (
            <Link to="/accedi" data-testid="feed-empty-login-cta" className="mt-6 inline-block rounded-full bg-[#B85328] px-7 py-3 text-sm font-semibold text-[#FBF8F3] transition-all hover:bg-[#9C421C]">
              Accedi e condividi
            </Link>
          )}
        </div>
      )}
      {!loading && posts.length > 0 && (
        <>
        <FeaturedMemory post={featured} />
        <div data-testid="feed-grid" className="columns-1 gap-6 sm:columns-2 lg:columns-3 [&>*]:mb-6">
          {posts.map((post, i) => (
            <motion.article
              key={post.post_id}
              data-testid={`feed-post-card-${post.post_id}`}
              initial={CARD_HIDDEN}
              whileInView={CARD_VISIBLE}
              viewport={CARD_VIEWPORT}
              transition={cardTransition(i)}
              className="break-inside-avoid overflow-hidden rounded-3xl border border-[#E8DFD5] bg-white transition-all duration-300 hover:-translate-y-1 hover:border-[#B85328]/40 hover:shadow-xl"
            >
              <Link to={`/ricordo/${post.post_id}`} data-testid={`feed-post-link-${post.post_id}`} className="block">
                <PostMedia post={post} className="w-full object-cover" />
              </Link>
              <div className="p-5">
                <div className="flex items-baseline justify-between gap-3">
                  <Link to={`/cani/${encodeURIComponent(post.dog_name)}`} data-testid={`feed-dog-link-${post.post_id}`}>
                    <h3 className="font-display text-xl font-semibold text-[#2C2723] transition-colors hover:text-[#B85328]">{post.dog_name}</h3>
                  </Link>
                  <span className="text-xs text-[#8C7B6E]">di {post.author_name}</span>
                </div>
                {post.caption && <p className="mt-2 text-sm leading-relaxed text-[#5C534B]">{post.caption}</p>}
                <div className="mt-4 flex items-center gap-4 border-t border-[#E8DFD5] pt-4">
                  <button
                    data-testid={`feed-like-button-${post.post_id}`}
                    onClick={() => toggleLike(post)}
                    className={`flex items-center gap-1.5 text-sm font-semibold transition-all duration-300 ${
                      post.liked_by_me ? "text-[#B85328]" : "text-[#8C7B6E] hover:text-[#B85328]"
                    }`}
                  >
                    <Heart size={17} fill={post.liked_by_me ? "#B85328" : "none"} />
                    {post.likes_count}
                  </button>
                  <Link
                    to={`/ricordo/${post.post_id}`}
                    data-testid={`feed-comments-link-${post.post_id}`}
                    className="flex items-center gap-1.5 text-sm font-semibold text-[#8C7B6E] transition-colors hover:text-[#4A6B5D]"
                  >
                    <MessageCircle size={17} />
                    {post.comments_count}
                  </Link>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
        </>
      )}
    </main>
  );
};

export default Feed;
